/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useQuickList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing quick list modal functionality.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuthStore } from '../../../store/auth';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { fetchQuickList } from '../api/usersApi';
import { User } from '../types/users.types';

export function useQuickList(showQuickList: boolean) {
  const { user: currentUser } = useAuthStore();
  const { t } = useI18n();
  const { showError } = useNotifications();
  const tenantSlug = currentUser?.tenant?.slug || t('users.defaultTenant');

  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListUsers, setQuickListUsers] = useState<User[]>([]);
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);

  const tRef = useRef(t);
  const showErrorRef = useRef(showError);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  const loadQuickList = async () => {
    if (quickListLoadingRef.current) return;

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      const users = await fetchQuickList(tenantSlug, currentUser?.id, quickListSearch);
      setQuickListUsers(users);
    } catch (error) {
      showErrorRef.current(error?.message || tRef.current('users.errors.loadFailed'));
      setQuickListUsers([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  };

  useEffect(() => {
    if (showQuickList && quickListUsers.length === 0 && !quickListLoadingRef.current) {
      loadQuickList();
    }
  }, [showQuickList, quickListUsers.length]);

  const filteredQuickList = useMemo(() => {
    const users = quickListUsers.filter((u) => u.id != null);
    if (!quickListSearch.trim()) {
      return users;
    }
    const searchLower = quickListSearch.toLowerCase();
    return users.filter(
      (user) =>
        user.username?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.fullName?.toLowerCase().includes(searchLower) ||
        user.department?.toLowerCase().includes(searchLower),
    );
  }, [quickListUsers, quickListSearch]);

  return {
    quickListSearch,
    setQuickListSearch,
    filteredQuickList,
    isQuickListLoading,
  };
}

